import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, UsersRound } from "lucide-react";

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", members: [], manager_id: "", verticals: [] });
  const [verticalOptions, setVerticalOptions] = useState([]);

  const loadData = async () => {
    try {
      const [teamsRes, usersRes, vertRes] = await Promise.all([
        api.get("/teams"), api.get("/users"),
        api.get("/config/batch", { params: { groups: "crm_verticals" } }).catch(() => ({ data: {} })),
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data);
      setVerticalOptions(((vertRes.data || {}).crm_verticals || []).map(v => v.value).filter(Boolean));
    } catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditingTeam(null);
    setForm({ name: "", description: "", members: [], manager_id: "", verticals: [] });
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditingTeam(t);
    setForm({ name: t.name, description: t.description || "", members: t.members || [], manager_id: t.manager_id || "", verticals: t.verticals || [] });
    setShowDialog(true);
  };

  const toggleMember = (userId) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(userId) ? prev.members.filter(id => id !== userId) : [...prev.members, userId]
    }));
  };

  const handleSave = async () => {
    try {
      if (editingTeam) {
        await api.put(`/teams/${editingTeam._id}`, form);
        toast.success("Team updated");
      } else {
        await api.post("/teams", form);
        toast.success("Team created");
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team?")) return;
    try { await api.delete(`/teams/${id}`); toast.success("Team deleted"); loadData(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const getUserName = (id) => users.find(u => u._id === id)?.name || id;

  const toggleVertical = (v) => {
    setForm(prev => ({
      ...prev,
      verticals: prev.verticals.includes(v) ? prev.verticals.filter(x => x !== v) : [...prev.verticals, v],
    }));
  };

  return (
    <div data-testid="teams-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Teams</h1>
          <p className="text-sm text-zinc-500 mt-1">Organize users into teams for reporting</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-team-button">
          <Plus size={14} /> Add Team
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teams-grid">
        {loading ? (
          <p className="text-xs text-zinc-400 col-span-3 text-center py-8">Loading...</p>
        ) : teams.length === 0 ? (
          <p className="text-xs text-zinc-400 col-span-3 text-center py-8">No teams created yet</p>
        ) : teams.map((team) => (
          <div key={team._id} className="border border-zinc-200 p-4" data-testid={`team-card-${team._id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-zinc-100 flex items-center justify-center rounded-sm">
                  <UsersRound size={14} className="text-zinc-500" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sm text-zinc-900">{team.name}</h3>
                  <p className="text-[10px] text-zinc-500">{team.description}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(team)} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={12} className="text-zinc-400" /></button>
                <button onClick={() => handleDelete(team._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={12} className="text-red-400" /></button>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-1" data-testid={`team-manager-${team._id}`}>
                Manager: {team.manager_id ? getUserName(team.manager_id) : "—"}
              </p>
              <div className="flex gap-1 flex-wrap mb-2" data-testid={`team-verticals-${team._id}`}>
                {(team.verticals || []).length === 0
                  ? <span className="text-[10px] text-zinc-400">No CRM verticals — this team grants no extra CRM access</span>
                  : (team.verticals || []).map(v => (
                      <span key={v} className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">{v}</span>
                    ))}
              </div>
              <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-1">{team.members?.length || 0} Members</p>
              <div className="flex gap-1 flex-wrap">
                {(team.members || []).map(id => (
                  <span key={id} className="px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm">{getUserName(id)}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingTeam ? "Edit Team" : "Create Team"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Team Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="team-name-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" data-testid="team-description-input" />
            </div>
            <div>
              <Label className="text-xs">Manager</Label>
              <select
                value={form.manager_id}
                onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                className="mt-1 w-full h-9 border border-zinc-200 rounded-sm px-2 text-xs"
                data-testid="team-manager-select"
              >
                <option value="">No manager</option>
                {users.filter(u => u.status === "active").map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">CRM verticals this team works in</Label>
              <p className="text-[10px] text-zinc-400">The manager sees his members' parties only in these verticals.</p>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="team-verticals-picker">
                {verticalOptions.map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVertical(v)}
                    className={`px-2 py-1 text-[11px] border rounded-sm ${form.verticals.includes(v) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}
                    data-testid={`team-vertical-${v.toLowerCase().replace(/\s+/g, "-")}`}
                  >{v}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Members</Label>
              <div className="mt-2 border border-zinc-200 max-h-48 overflow-y-auto">
                {users.filter(u => u.status === 'active').map(u => (
                  <label key={u._id} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 cursor-pointer border-b border-zinc-50">
                    <input type="checkbox" checked={form.members.includes(u._id)} onChange={() => toggleMember(u._id)} className="rounded-sm" />
                    <span className="text-xs text-zinc-700">{u.name}</span>
                    <span className="text-[10px] text-zinc-400">{u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-team-button">Save Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
